import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { ArrowRightIcon, CheckCircleIcon, ChevronRightIcon, SearchIcon, AlertCircleIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { sectorPages, getSectorPageBySlug } from '../data/sectorPages';
import { getClientCaseBySlug } from '../data/allClientCases';
import { buildBreadcrumbList, buildServiceSchema } from '../lib/structuredData';
export const SectorPage: React.FC = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const page = getSectorPageBySlug(slug || '');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!page) {
    return <Navigate to="/websites" replace data-id="element-1817" data-mp="2xx2sp2r2opjx" />;
  }
  const relatedCase = page.caseSlug ? getClientCaseBySlug(page.caseSlug) : undefined;
  const anderePaginas = sectorPages.filter(p => p.slug !== page.slug).slice(0, 6);
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: page.faq.map(f => ({
      '@type': 'Question',
      name: f.vraag,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.antwoord
      }
    }))
  };
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-1818" data-mp="2y78ic59iybpy">
      <SEO title={page.metaTitle} description={page.metaDescription} canonical={`https://noorddigital.nl/website-voor/${page.slug}`} jsonLd={[buildServiceSchema({
      name: page.h1,
      description: page.metaDescription,
      path: `/website-voor/${page.slug}`,
      serviceType: `Webdesign voor ${page.sector}`
    }), buildBreadcrumbList([{
      name: 'Websites',
      path: '/websites'
    }, {
      name: page.sector,
      path: `/website-voor/${page.slug}`
    }]), faqSchema]} data-id="element-1819" data-mp="2ahxmn27177io" />
      <Header data-id="element-1820" data-mp="1uhpehqsddye" />

      {/* Hero */}
      <section className="w-full bg-gradient-to-br from-[#007AFF] to-[#1B2B44] py-16 md:py-20" data-id="element-1821" data-mp="1t6mek560a8c">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1822" data-mp="6rrm8igi8rw9">
          <nav aria-label="Kruimelpad" className="mb-8" data-id="element-1823" data-mp="brw68igmg4r8">
            <ol className="flex items-center gap-1.5 text-sm text-white text-opacity-70 flex-wrap" data-id="element-1824" data-mp="bqbd8x9ygtvh">
              <li data-id="element-1825" data-mp="gow96ki6cre4">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-1826" data-mp="3f94hukkra8di">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-1827" data-mp="3k8h6nav6tpiq">
                <ChevronRightIcon className="h-3.5 w-3.5 text-white text-opacity-40" data-id="element-1828" data-mp="3p728tfu02j9t" />
              </li>
              <li data-id="element-1829" data-mp="3txsdequlqi3g">
                <Link to="/websites" className="hover:text-white transition-colors" data-id="element-1830" data-mp="3tyaf43019oir">
                  Websites
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-1831" data-mp="2wgagwujywgsa">
                <ChevronRightIcon className="h-3.5 w-3.5 text-white text-opacity-40" data-id="element-1832" data-mp="31gy4f68zmfhr" />
              </li>
              <li className="font-medium text-white" aria-current="page" data-id="element-1833" data-mp="1imv7w9oyiklb">
                {page.sector}
              </li>
            </ol>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-1834" data-mp="1eini096riyli">
            <div className="lg:col-span-7" data-id="element-1835" data-mp="19k3x8c3wnsuq">
              <h1 className="font-bold text-white leading-tight mb-6 text-4xl md:text-5xl" data-id="element-1836" data-mp="14mua533znta2">
                {page.h1}
              </h1>
              <p className="text-white text-opacity-90 text-lg leading-relaxed mb-8" data-id="element-1837" data-mp="zoix6jied2nm">
                {page.intro}
              </p>
              <div className="flex flex-wrap gap-4" data-id="element-1838" data-mp="uuo78yc77rg3">
                <Link to="/prijsscan" className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-lg" data-id="element-1839" data-mp="qudzph8rcsz4">
                  Start de website scan
                  <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-1840" data-mp="gw857kpa6p4a" />
                </Link>
                <a href="/#contact" className="inline-flex items-center px-6 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg" data-id="element-1841" data-mp="bygc4ncwgwyi">
                  Plan een kennismaking
                </a>
              </div>
            </div>

            {/* Herkenbaar */}
            <div className="lg:col-span-5" data-id="element-1842" data-mp="279fy1etxq47o">
              <div className="bg-white rounded-2xl p-8 shadow-2xl" data-id="element-1843" data-mp="278ec2fbdrxb6">
                <div className="flex items-center gap-2 mb-5" data-id="element-1844" data-mp="1t9y3eityjwqe">
                  <AlertCircleIcon className="h-5 w-5 text-[#007AFF]" data-id="element-1845" data-mp="1tbrce4s1mt1k" />
                  <p className="text-sm font-semibold text-[#111827]" data-id="element-1846" data-mp="1y9j2qud1i73n">
                    Herken je dit?
                  </p>
                </div>
                <ul className="space-y-3" data-id="element-1847" data-mp="1oalk282ap7sk">
                  {page.herkenbaar.map(item => <li key={item} className="flex items-start gap-3" data-id="element-1848" data-mp="3dpi85xv9vp07">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#007AFF] flex-shrink-0" data-id="element-1849" data-mp="3dqb4e4wluw43" />
                      <span className="text-sm text-[#6B7280] leading-relaxed" data-id="element-1850" data-mp="3intg2320bpfs">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wat er op de site hoort */}
      <section className="w-full bg-white py-20 md:py-24" data-id="element-1851" data-mp="1gsvnfvg8l20j">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1852" data-mp="1byri1gmmd21k">
          <div className="max-w-3xl mb-14" data-id="element-1853" data-mp="1pqplbatng3pt">
            <h2 className="font-bold text-[#111827] text-3xl md:text-4xl mb-4" data-id="element-1854" data-mp="1pttteja8jjlr">
              Wat er op de site van een {page.sector.toLowerCase()} thuishoort
            </h2>
            <p className="text-[#6B7280] text-lg leading-relaxed" data-id="element-1855" data-mp="1zpet2aru07sc">
              Niet elk onderdeel is voor elk bedrijf even belangrijk. In de
              kennismaking bepalen we samen waar de winst voor jou zit.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch" data-id="element-1856" data-mp="d2j7bu9348g">
            {page.onderdelen.map((o, i) => <motion.div key={o.titel} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.4,
            delay: Math.min(i, 4) * 0.05
          }} className="bg-[#F7F8FA] rounded-2xl p-8 border border-gray-100 flex flex-col" data-id="element-1857" data-mp="20g3soldwlomy" data-mpi={i}>
                <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center mb-5" data-id="element-1858" data-mp="2jcv98n229pnb">
                  <CheckCircleIcon className="h-5 w-5 text-[#007AFF]" data-id="element-1859" data-mp="2ob6hwugmdu3y" />
                </div>
                <h3 className="font-bold text-[#111827] text-lg mb-3" data-id="element-1860" data-mp="2t49cn5x4v9lq">
                  {o.titel}
                </h3>
                <p className="text-[#6B7280] leading-relaxed" data-id="element-1861" data-mp="2x7fdtz5e2alc">{o.tekst}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Vindbaarheid */}
      <section className="w-full bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] py-20 md:py-24" data-id="element-1862" data-mp="373s74vx0umfk">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1863" data-mp="3h8qrtab8bq2w">
          <div className="max-w-3xl" data-id="element-1864" data-mp="3c7kzvepe86uf">
            <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center mb-6" data-id="element-1865" data-mp="3cap8qpdszqrf">
              <SearchIcon className="h-6 w-6 text-[#007AFF]" data-id="element-1866" data-mp="337xvql5a4mxo" />
            </div>
            <h2 className="font-bold text-[#111827] text-3xl md:text-4xl mb-5" data-id="element-1867" data-mp="33a0sdo0xtebd">
              Hoe klanten in deze branche zoeken
            </h2>
            <p className="text-[#4B5563] text-lg leading-relaxed" data-id="element-1868" data-mp="3uzvmdxzqps6d">
              {page.vindbaarheid}
            </p>
          </div>
        </div>
      </section>

      {/* Case als bewijs */}
      {relatedCase && <section className="w-full bg-white py-20 md:py-24" data-id="element-1869" data-mp="3m60q7fgljc2v">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1870" data-mp="mjkd4whygkm5">
            <div className="bg-[#F7F8FA] rounded-2xl overflow-hidden border border-gray-100 grid grid-cols-1 lg:grid-cols-2" data-id="element-1871" data-mp="hskenmt6tuhj">
              <div className="h-64 lg:h-auto" data-id="element-1872" data-mp="11g2xh89x7d65">
                <img src={relatedCase.heroImage} alt={`${relatedCase.clientName}, klantcase Noord Digital`} loading="lazy" className="w-full h-full object-cover object-top" data-id="element-1873" data-mp="11e9mxhw6s8sv" />
              </div>
              <div className="p-8 md:p-12 flex flex-col justify-center" data-id="element-1874" data-mp="whj6uukdtd7g">
                <p className="text-sm font-semibold text-[#007AFF] mb-3" data-id="element-1875" data-mp="1k7vpp0h60uct">
                  Dit deden we voor een {page.sector.toLowerCase()}
                </p>
                <h2 className="font-bold text-[#111827] text-2xl md:text-3xl mb-4" data-id="element-1876" data-mp="1a8o8213eedjw">
                  {page.caseNaam}
                </h2>
                <p className="text-[#6B7280] leading-relaxed mb-8" data-id="element-1877" data-mp="1f6pqcjt028ub">
                  {page.caseTekst}
                </p>
                <Link to={`/cases/${relatedCase.slug}`} className="inline-flex items-center gap-2 text-[#007AFF] font-semibold w-fit" data-id="element-1878" data-mp="3uxkdf5drcvqr">
                  Lees de volledige case
                  <ArrowRightIcon className="h-4 w-4" data-id="element-1879" data-mp="2d18ynueb2xx9" />
                </Link>
              </div>
            </div>
          </div>
        </section>}

      {/* FAQ */}
      <section className="w-full bg-[#F7F8FA] py-20 md:py-24" data-id="element-1880" data-mp="2r08jss2m0uoo">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[820px]" data-id="element-1881" data-mp="2n0hocb251qdt">
          <h2 className="font-bold text-[#111827] text-3xl md:text-4xl mb-10" data-id="element-1882" data-mp="1z9c83usacw2z">
            Veelgestelde vragen
          </h2>
          <div className="space-y-4" data-id="element-1883" data-mp="1ubu0q8tx2ox3">
            {page.faq.map(f => <details key={f.vraag} className="bg-white rounded-xl border border-gray-200 p-6 group" data-id="element-1884" data-mp="282pcbsvsvk4d">
                <summary className="font-semibold text-[#111827] cursor-pointer list-none flex items-center justify-between gap-4" data-id="element-1885" data-mp="23a5tohi99ysp">
                  {f.vraag}
                  <ChevronRightIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0 group-open:rotate-90 transition-transform" data-id="element-1886" data-mp="1bj85nqqu32dk" />
                </summary>
                <p className="text-[#6B7280] leading-relaxed mt-4" data-id="element-1887" data-mp="lm9xb90rmb4b">
                  {f.antwoord}
                </p>
              </details>)}
          </div>
        </div>
      </section>

      {/* Andere sectoren */}
      <section className="w-full bg-white py-20 md:py-24" data-id="element-1888" data-mp="7pec6i78ot7w">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1889" data-mp="7qz5y5lqc84p">
          <h2 className="font-bold text-[#111827] text-2xl md:text-3xl mb-8" data-id="element-1890" data-mp="2rcvu5177804">
            Ook voor deze branches
          </h2>
          <div className="flex flex-wrap gap-3" data-id="element-1891" data-mp="3u2mp4lq8rcqf">
            {anderePaginas.map(p => <Link key={p.slug} to={`/website-voor/${p.slug}`} className="inline-flex items-center gap-2 bg-[#F7F8FA] hover:bg-[#E8F0FE] border border-gray-200 text-[#111827] font-medium px-5 py-3 rounded-lg transition-colors" data-id="element-1892" data-mp="3p30964j13idx">
                {p.sector}
                <ArrowRightIcon className="h-4 w-4 text-[#007AFF]" data-id="element-1893" data-mp="1qnt14g0z6kxz" />
              </Link>)}
          </div>
        </div>
      </section>

      {/* Slot-CTA */}
      <section className="w-full bg-gradient-to-br from-[#007AFF] to-[#005FCC] py-16 md:py-20" data-id="element-1894" data-mp="1voqiwri2pcyy">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] text-center" data-id="element-1895" data-mp="20jmbbsr822cl">
          <h2 className="font-bold text-white text-3xl md:text-4xl mb-4" data-id="element-1896" data-mp="24mixtujvuilt">
            Benieuwd wat dit voor jouw bedrijf oplevert?
          </h2>
          <p className="text-white text-opacity-90 text-lg leading-relaxed mb-8 max-w-2xl mx-auto" data-id="element-1897" data-mp="1cvmkytxtodyj">
            In een gesprek van een half uur kijken we naar je huidige
            vindbaarheid en vertellen we wat we zouden aanpakken. Je zit daarna
            tot niets verplicht.
          </p>
          <a href="/#contact" className="inline-flex items-center px-8 py-4 bg-white text-[#007AFF] font-semibold rounded-lg shadow-lg" data-id="element-1898" data-mp="p73dggmlk57i">
            Plan een kennismaking
            <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-1899" data-mp="1wijbhvfg4myl" />
          </a>
        </div>
      </section>

      <Footer data-id="element-1900" data-mp="1rkhqv62dgfl0" />
    </div>;
};